
import React from 'react'
import { useRouter } from 'next/navigation'
import { useGeolocation, getGeolocationStatus } from '@/hooks/geolocation/useGeolocation'
import { IScript } from '@/types/cinematics'
import { useTranslation } from '@/localization/useTranslation'
import { debug } from '@/utils/debug'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLocationPinLock } from '@fortawesome/free-solid-svg-icons'
import { Button } from '@/components/Buttons/Button'
import { Spinner } from '@/components/Loading/Spinner'

import classes from './styles.module.scss'

interface IGeoLocationGuardProps {
  children: React.ReactNode
  script?: Partial<IScript>
  deny?: string[]
  allow?: string[]
  message?: React.ReactNode | ((data: { allow: string[], deny: string[], userCountry?: string }) => React.ReactNode) | null | false
  skeleton?: React.ReactNode
}

interface DefaultGeoBlockMessageProps {
  allow: string[]
  deny: string[]
  userCountry?: string
}

const DefaultGeoBlockMessage: React.FC<DefaultGeoBlockMessageProps> = ({ allow, deny, userCountry }) => {
  const { t } = useTranslation()
  const router = useRouter()

  const handleBackToHome = () => {
    router.push('/')
  }

  debug.log({
    allow,
    deny,
    yourCountry: userCountry
  })

  return (
    <div className={classes.container}>
      <FontAwesomeIcon size='4x' icon={faLocationPinLock} />
      <h3>{t('Content not available')}</h3>
      <p>{t('This content is not accessible from your location')}</p>
      <Button onClick={handleBackToHome}>{t('Back to Home')}</Button>
    </div>
  )
}

export const GeoLocationGuard = (props: IGeoLocationGuardProps) => {
  const { children, script, deny, allow, message, skeleton } = props
  const { geolocation, isLoading } = useGeolocation()
  const allowedCountries = script?.options?.geolocation?.allow || allow || []
  const restrictedCountries = script?.options?.geolocation?.deny || deny || []

  const country = geolocation?.country?.iso_code
  // Shared with useGeolocation so the fail-open rule lives in one place: an
  // unknown country (geo outage, iCloud Private Relay) must never hide UI.
  const { isAllowed } = getGeolocationStatus(geolocation, {
    allow: allowedCountries,
    deny: restrictedCountries
  })

  if (isLoading) {
    // No skeleton from the caller still must not render a blank page — Director
    // gates whole scenes with this, and `computeAppLoadingState` does not wait
    // on geolocation, so nothing else covers the gap.
    //
    // `isLoading` is `pending && fetching`, so anything that resets
    // `['geolocation']` lands here again and unmounts every actor in the scene,
    // discarding their state. Every logout path used to do exactly that, which
    // on `/entel` (geo-gated *and* `forceLogout`) meant every logged-in arrival
    // saw spinner -> form -> spinner -> remount. The key is now excluded from
    // the teardown — see `resetUserScopedQueries`. Anything else that resets it
    // will reintroduce the unmount.
    return skeleton
      ? <>{skeleton}</>
      : (
        <div className={classes.loading}>
          <Spinner />
        </div>
      )
  }

  if (!isAllowed) {
    // Explicitly return nothing if message is null or false
    if (message === null || message === false) {
      return null
    }

    // Use custom message if provided
    if (message !== undefined) {
      const messageContent = typeof message === 'function'
        ? message({ allow: allowedCountries, deny: restrictedCountries, userCountry: country })
        : message

      return (
        <>
          {messageContent}
        </>
      )
    }

    // Use default message when message is undefined
    return (
      <DefaultGeoBlockMessage
        allow={allowedCountries}
        deny={restrictedCountries}
        userCountry={country}
      />
    )
  }

  return (
    <>
      {children}
    </>
  )
}
